/**
 * Der Empfänger des Drawers.
 *
 * `main.tsx` hängt den Drawer über die ganze Anwendung, damit er von jeder
 * Ansicht aus zu öffnen ist. Bis zum 13.08.2026 hing er dort **ohne**
 * `onSpeichern` — alle neun Formulare prüften die Eingaben vollständig und
 * meldeten dann „Noch nicht gespeichert".
 *
 * Dieser Baustein schließt die Lücke. Er steht zwischen Drawer und
 * Schnittstelle, weil beides nicht dasselbe weiß:
 *
 *   - Der Drawer kennt Formularschlüssel und Werte, aber keinen Mandanten und
 *     keine Meldungen.
 *   - `api/anlegen.ts` kennt die Abbildung auf die Sammlungen, aber nicht,
 *     was danach zu sagen ist.
 *
 * ## Drei Auskünfte, die auseinandergehalten werden
 *
 * 1. **Angelegt.** Der Datensatz steht, mit seiner Kennung.
 * 2. **Zur Freigabe eingereicht.** Er steht **nicht** — jemand muss zustimmen.
 *    Das als „angelegt" zu melden wäre die Art Falschmeldung, gegen die
 *    `bausteine/nochNicht.ts` geschrieben wurde.
 * 3. **Kein Weg vorhanden.** Für dieses Formular gibt es keine Anbindung. Auch
 *    das ist eine ehrliche Auskunft und keine Fehlermeldung über die Eingabe.
 *
 * ## Rollen am Asset (seit dem 07.10.2026)
 *
 * Ein Asset-Formular trägt die Auswahl „Rollen zuordnen". Gespeichert wird
 * in zwei Schritten mit **einer** Vorgangskennung: erst das Asset, dann die
 * Rollen über `POST /api/zuordnungen/_sammeln`. Scheitert der zweite Schritt,
 * bleibt das Fenster offen und wechselt in den Bearbeiten-Modus — das Asset
 * steht ja schon, und ein zweites „Speichern" legte sonst ein zweites an.
 */

import { useNavigate } from 'react-router-dom';
import { Drawer, type AssetKennung, type Bezug, type Speicherantwort } from './Drawer';
import { useToast } from './Toast';
import {
  ausFormularAnlegen, ausFormularAendern, assetTypAusFormular, kategorieAusFormular, ABBILDUNGEN,
  OHNE_WEG, zielRoute,
} from '../api/anlegen';
import { alleSeitenHolen, alleSeitenPfad, bestandGeaendert } from '../api/haken';
import { ApiFehler } from '../api/klient';
import { tokenHolen } from '../anmeldung/msal';
import { mandantAusSpeicher } from '../entwurf/mandant';
import { sammelText, zuordnungenSammeln } from '../api/sammelzuordnung';
import { planBelegt, type Rollenplan } from './Rollenzuordnung';

/** Ein Text, wie ihn ein Satz der Schnittstelle führt — oder der Ersatz. */
const text = (wert: unknown, ersatz: string): string =>
  typeof wert === 'string' && wert !== '' ? wert : ersatz;

/**
 * Die Rollen eines Assets schreiben — ein Sammelvorgang.
 *
 * Gibt `null` zurück, wenn es geklappt hat, sonst den Plan, mit dem das
 * Fenster offen bleibt: bei einem Konflikt (409) ohne die Entfernungen, deren
 * Version veraltet ist — die Auswahl dazu ist neu zu treffen.
 */
async function rollenSchreiben(
  mandant: string,
  asset: AssetKennung,
  plan: Rollenplan,
  vorgangId: string | undefined,
  melden: ReturnType<typeof useToast>,
): Promise<Rollenplan | null> {
  try {
    const e = await zuordnungenSammeln(mandant, {
      asset: asset.name,
      assetTyp: asset.assetTyp,
      kategorie: asset.kategorie,
      hinzufuegen: plan.hinzu.map((rolleId) => ({ rolleId, berechtigungsart: plan.art })),
      entfernen: Object.entries(plan.weg).map(([berechtigungId, version]) => ({ berechtigungId, version })),
      begruendung: plan.grund.trim(),
      ...(vorgangId ? { vorgangId } : {}),
    });
    melden(e.freigabe ? 'Rollen zur Freigabe eingereicht' : 'Rollen gespeichert',
      `„${asset.name}": ${sammelText(e)}`, e.freigabe ? 'hinweis' : 'erfolg');
    bestandGeaendert('zuordnungen');
    bestandGeaendert('rollen');
    if (e.freigabe) bestandGeaendert('freigaben');
    return null;
  } catch (fehler) {
    if (fehler instanceof ApiFehler && fehler.status === 409) {
      melden('Rollen nicht gespeichert',
        'Eine der Zuordnungen, die entfernt werden sollten, hat inzwischen jemand anders '
        + 'geändert. Es wurde nichts geschrieben. Die Auswahl zum Entfernen ist '
        + 'zurückgesetzt — bitte prüfen und erneut speichern.', 'fehler');
      bestandGeaendert('zuordnungen');
      return { ...plan, weg: {}, grund: '' };
    }
    melden('Rollen nicht gespeichert',
      fehler instanceof Error ? fehler.message : 'Unbekannter Fehler.', 'fehler');
    return plan;
  }
}

/**
 * Nach einem Umbenennen tragen die Zuordnungen neue Versionen: die
 * Schnittstelle zieht sie auf den neuen Namen nach. Die Entfernungen im Plan
 * hängen an den alten — sie werden hier auf den jetzigen Stand gebracht,
 * **nur** für Zeilen, die es unter dem neuen Namen gibt.
 */
async function versionenNachziehen(
  mandant: string, asset: AssetKennung, plan: Rollenplan,
): Promise<Rollenplan> {
  if (Object.keys(plan.weg).length === 0) return plan;
  const seite = await alleSeitenHolen<{ berechtigungId: string; __version?: string | null }>(
    alleSeitenPfad('zuordnungen', {
      filter: {
        asset: asset.name, assetTyp: asset.assetTyp,
        ...(asset.kategorie ? { kategorie: asset.kategorie } : {}),
      },
    }),
    { mandant, token: await tokenHolen() },
  );
  const jetzt = new Map(seite.zeilen.map((z) => [z.berechtigungId, String(z.__version ?? '')]));
  const weg: Record<string, string> = {};
  for (const [id, version] of Object.entries(plan.weg)) weg[id] = jetzt.get(id) || version;
  return { ...plan, weg };
}

export function Anlegeweg({ children }: { children: React.ReactNode }) {
  const melden = useToast();
  const navigieren = useNavigate();

  return (
    <Drawer
      /* Der Drawer **wartet** auf diese Zusage und schliesst erst danach.
         `false` heisst: es blieb etwas zu tun, das Fenster bleibt mit allen
         Eingaben stehen. Vorher lief der Schreibvorgang neben einem bereits
         geschlossenen Fenster; wer zwanzig Felder gefuellt hatte und von der
         Schnittstelle abgewiesen wurde, fuellte sie erneut. Der Kunde hat es
         am 27.08.2026 gemeldet. */
      onSpeichern={async (schluessel, werte, bearbeiten, bezug, zusatz): Promise<boolean | Speicherantwort> => {
        const mandant = mandantAusSpeicher();
        const sammlung = ABBILDUNGEN[schluessel]?.sammlung;
        const plan = sammlung === 'assets' && planBelegt(zusatz.rollen) ? zusatz.rollen : null;
        {
          /* --- Bearbeiten -------------------------------------------------
           *
           * Bis zum 28.08.2026 stand hier nichts. Die Rueckmeldung wurde als
           * `(schluessel, werte)` entgegengenommen — der dritte Parameter
           * `bearbeiten` kam an und wurde **verworfen**, und danach lief in
           * jedem Fall `ausFormularAnlegen`.
           *
           * Nichts scheiterte dabei, nichts meldete sich: der Bestand hatte
           * hinterher eine Zeile mehr statt eine geaenderte. Der Kunde hat es
           * am Warenkorb eines Ausstattungspakets gemerkt; betroffen war
           * **jedes** Feld jedes Assets, das ueber den Drawer bearbeitet
           * wurde.
           *
           * Fehlt der Bezug, wird **nicht** ersatzweise angelegt. Ein Satz,
           * dessen Schluessel niemand kennt, laesst sich nicht aendern — und
           * daraus ein Anlegen zu machen ist genau der Fehler, der hier
           * behoben wird. */
          if (bearbeiten) {
            if (!bezug) {
              melden('Nicht gespeichert',
                'Zu diesem Datensatz fehlen Schluessel und Zeilenversion. Ohne sie '
                + 'ließe sich weder sagen, welche Zeile gemeint ist, noch ob '
                + 'inzwischen jemand anders geschrieben hat. Bitte die Liste neu '
                + 'laden und erneut öffnen.', 'fehler');
              return false;
            }

            /* Nur Rollen gewählt, kein Feld geändert: dann geht keine leere
               Änderung an das Asset — sie meldete „Nichts zu ändern" neben
               einer Zuordnung, die sehr wohl geschah. */
            const asset = bezug.asset;
            if (plan && asset && !zusatz.felderGeaendert) {
              const rest = await rollenSchreiben(mandant, asset, plan, undefined, melden);
              if (rest) return { offen: true, rollen: rest };
              bestandGeaendert('assets');
              return true;
            }

            let neuerBezug: Bezug = bezug;
            let vorgangId: string | undefined;
            let assetJetzt: AssetKennung | undefined = asset;
            try {
              const ergebnis = await ausFormularAendern(
                mandant, schluessel, bezug.schluessel, bezug.version, werte,
              );
              vorgangId = ergebnis.vorgangId || undefined;
              if (ergebnis.freigabe) {
                melden('Zur Freigabe eingereicht',
                  `Die Änderung an „${bezug.schluessel}" ist noch nicht wirksam — `
                  + `${ergebnis.freigabe.felder.length} ${ergebnis.freigabe.felder.length === 1
                    ? 'Feld liegt' : 'Felder liegen'} zur Zustimmung. `
                  + 'Der offene Vorgang steht unter „Änderungsfreigaben".', 'hinweis');
                bestandGeaendert('freigaben');
              } else {
                const felder = ergebnis.geaendert?.length ?? 0;
                melden(
                  felder === 0 ? 'Nichts zu ändern' : 'Gespeichert',
                  felder === 0
                    ? 'Der Stand in der Datenbank ist bereits derselbe.'
                    : `${felder} ${felder === 1 ? 'Feld' : 'Felder'} übernommen.`,
                  'erfolg',
                );
                if (sammlung) bestandGeaendert(sammlung);
                /* Was jetzt gilt — Name und Kategorie koennen sich geaendert
                   haben, die Version hat es sicher. */
                const satz = ergebnis.satz ?? {};
                if (asset) {
                  assetJetzt = {
                    name: text(satz['name'], asset.name),
                    assetTyp: text(satz['assetTyp'], asset.assetTyp),
                    kategorie: text(satz['kategorie'], asset.kategorie),
                  };
                }
                neuerBezug = {
                  ...bezug,
                  version: text(satz['__version'], bezug.version),
                  ...(assetJetzt ? { asset: assetJetzt } : {}),
                };
              }
            } catch (fehler) {
              /* Ein Konflikt ist keine Fehlermeldung, sondern eine
                 Entscheidung — der Drawer zeigt den Konfliktweg. */
              if (fehler instanceof ApiFehler && fehler.status === 409) return { konflikt: fehler };
              melden('Nicht gespeichert',
                fehler instanceof Error ? fehler.message : 'Unbekannter Fehler.',
                'fehler');
              /* Offen lassen — wie beim Anlegen: der Grund steht in der
                 Meldung, die Eingaben stehen im Formular. */
              return false;
            }

            if (plan && assetJetzt) {
              /* Umbenannt? Dann tragen die Zuordnungen neue Versionen. */
              let planJetzt = plan;
              if (asset && assetJetzt.name !== asset.name) {
                try {
                  planJetzt = await versionenNachziehen(mandant, assetJetzt, plan);
                } catch { /* dann entscheidet die Schnittstelle mit 409 */ }
              }
              const rest = await rollenSchreiben(mandant, assetJetzt, planJetzt, vorgangId, melden);
              if (rest) return { offen: true, bezug: neuerBezug, rollen: rest };
            }
            return true;
          }

          if (OHNE_WEG[schluessel]) {
            melden('Noch nicht anlegbar', OHNE_WEG[schluessel] as string, 'hinweis');
            /* Hier ist wirklich nichts mehr zu tun — die Objektart hat keinen
               Weg. Das Fenster offenzuhalten hiesse, auf etwas zu warten, was
               nicht kommt. */
            return true;
          }
          try {
            /* Ein Vorgang fuer Asset und Rollen — wie Rolle und Positionen
               im Rolleneditor. */
            const vorgang = plan ? crypto.randomUUID() : undefined;
            const ergebnis = await ausFormularAnlegen(
              mandant, schluessel, werte, undefined, vorgang,
            );

            if (ergebnis.freigabe) {
              melden(
                'Zur Freigabe eingereicht',
                `„${ergebnis.schluessel}" ist noch nicht angelegt — diese Objektart `
                + 'ist freigabepflichtig. Der Datensatz entsteht, sobald jemand '
                + 'anderes zustimmt; bis dahin steht er unter Änderungsfreigaben.',
                'hinweis',
              );
              /* Die Rollen gehen mit in die Freigabe: dieselbe Vorgangs-
                 kennung, und die Schnittstelle erkennt das Asset daran, auch
                 solange es noch nicht besteht. Scheitert es, sagt die Meldung
                 warum — das Fenster bleibt trotzdem nicht offen, denn ein
                 Bearbeiten gibt es erst, wenn das Asset steht. */
              if (plan) {
                const satz = ergebnis.satz ?? {};
                const nf = Object.entries(ABBILDUNGEN[schluessel]?.felder ?? {})
                  .find(([, f]) => f === 'name')?.[0];
                await rollenSchreiben(mandant, {
                  name: text(satz['name'], (nf ? werte[nf] ?? '' : '').trim().replace(/\s+/g, ' ')),
                  assetTyp: text(satz['assetTyp'], assetTypAusFormular(schluessel, werte) ?? ''),
                  kategorie: text(satz['kategorie'], kategorieAusFormular(schluessel, werte)),
                }, plan, ergebnis.vorgangId || vorgang, melden);
              }
              /* Der Postkorb hat eine Zeile mehr — die Liste dort soll das
                 sehen, auch wenn sie gerade offen ist. */
              bestandGeaendert('freigaben');
              navigieren('/genehmigungen');
              return true;
            }

            melden('Angelegt', `„${ergebnis.schluessel}" steht im Bestand.`, 'erfolg');
            /* Erst die Listen benachrichtigen, dann springen: wer schon auf
               der Zielseite steht, bekommt sonst keine Nachladung — die
               Adresse aendert sich ja nicht. */
            if (sammlung) bestandGeaendert(sammlung);

            if (plan) {
              const satz = ergebnis.satz ?? {};
              const asset: AssetKennung = {
                name: text(satz['name'], ''),
                assetTyp: text(satz['assetTyp'], assetTypAusFormular(schluessel, werte) ?? ''),
                kategorie: text(satz['kategorie'], kategorieAusFormular(schluessel, werte)),
              };
              const rest = await rollenSchreiben(
                mandant, asset, plan, ergebnis.vorgangId || vorgang, melden);
              if (rest) {
                /* Das Asset steht, die Rollen nicht. Das Fenster bleibt offen
                   und bearbeitet ab jetzt **dieses** Asset — ein zweites
                   „Speichern" legte sonst ein zweites an. */
                return {
                  offen: true,
                  bezug: {
                    schluessel: ergebnis.schluessel,
                    version: text(satz['__version'], ''),
                    asset,
                  },
                  rollen: rest,
                };
              }
              bestandGeaendert('assets');
            }

            const ziel = zielRoute(schluessel);
            if (ziel) navigieren(ziel);
            return true;
          } catch (fehler) {
            /* Der Grund kommt von der Schnittstelle und ist bewusst wörtlich:
               „Zum Anlegen fehlt das Recht assets.schreiben" sagt, was zu
               beantragen ist. Eine eigene Formulierung darüber verwischt das. */
            melden(
              'Nicht angelegt',
              fehler instanceof Error ? fehler.message : 'Unbekannter Fehler.',
              'fehler',
            );
            /* Offen lassen: der Grund steht in der Meldung, die Eingaben
               stehen im Formular, und beides zusammen laesst sich beheben. */
            return false;
          }
        }
      }}
    >
      {children}
    </Drawer>
  );
}
